<template>
  <v-card>
    <v-form
      @submit.prevent="onUpdate"
      lazy-validation
      ref="form"
      v-model="isValidForm"
    >
      <template v-if="!$apollo.loading">
        <v-card-title class="sticky top-0">Upphittat föremål</v-card-title>
        <v-divider />
        <div class="overflow-y-auto p-4" :style="{ height: tableHeight }">
          <v-form>
            <v-row>
              <v-col>
                <div class="py-4">
                  <v-img
                    v-if="lostProperty.image_md"
                    class="rounded shadow bg-gray-10"
                    height="200"
                    width="200"
                    :src="lostProperty.image_md"
                  ></v-img>
                  <div
                    v-else
                    style="width: 200px; height: 200px"
                    class="bg-gray-10 rounded shadow flex items-center justify-center flex-col"
                  >
                    <v-icon>mdi-image-off-outline</v-icon>
                    <span>Ingen bild tillgänglig</span>
                  </div>
                </div>
                <v-text-field
                  v-model="lostProperty.uuid"
                  disabled
                  label="UUID"
                ></v-text-field>
                <v-select
                  item-text="name"
                  item-value="uuid"
                  @change="() => (model.category_uuid = null)"
                  :items="categoryGroups"
                  v-model="category_group_uuid"
                  :rules="[validationRules.required]"
                  label="Kategori"
                ></v-select>
                <v-select
                  item-text="name"
                  item-value="uuid"
                  :items="filteredSubCategories"
                  v-model="model.category_uuid"
                  :rules="[validationRules.required]"
                  label="Underkategori"
                ></v-select>
                <v-textarea
                  label="Beskriv föremålet (Fritext)"
                  rows="4"
                  placeholder="Kännetecken, färg, modell, serienummer. skick etc."
                  v-model="model.description"
                ></v-textarea>
                <v-text-field
                  required
                  :rules="[validationRules.required, validationRules.dateTime]"
                  v-model="model.found_at"
                  type="datetime-local"
                  label="När förlorade du föremålet?"
                ></v-text-field>
                <v-radio-group
                  v-model="model.transportation_type_uuid"
                  mandatory
                >
                  <p>Färdsätt</p>
                  <v-radio
                    :label="transportationType.name"
                    :value="transportationType.uuid"
                    v-for="(transportationType, $i) in Object.values(
                      transportationTypes,
                    )"
                    :key="$i"
                  ></v-radio>
                </v-radio-group>
                <v-text-field
                  label="Linje/tågnummer"
                  v-model="model.transportation_number"
                ></v-text-field>
                <v-textarea
                  label="Yttligare information"
                  placeholder="Ex. Sträcka, hållplats"
                  rows="4"
                  v-model="model.transportation_information"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </div>
        <v-divider />
        <v-card-actions class="p-4">
          <v-spacer />
          <v-dialog v-model="showDestructModal" width="500" persistent>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on">Ta bort</v-btn>
            </template>
            <LostPropertiesDestructConfirm
              :uuid="$route.params.uuid"
              @close="showDestructModal = false"
              v-if="showDestructModal"
            />
          </v-dialog>

          <v-btn
            type="submit"
            color="primary"
            :disabled="!isFormChanges"
            :loading="updating"
            >Uppdatera</v-btn
          >
        </v-card-actions>
      </template>
    </v-form>
  </v-card>
</template>

<script>
import GET_CATEGORIES from '@/gql/categories/queries/GetCategories.gql'
import GET_CATEGORY_GROUPS from '@/gql/categoryGroups/queries/GetCategoryGroups.gql'
import GET_TRANSPORTATION_TYPES from '@/gql/transportationTypes/queries/GetTransportationTypes.gql'
import UPDATE_LOST_PROPERTY from '@/gql/lostProperties/mutations/UpdateLostProperty.gql'
import LostPropertiesDestructConfirm from './LostPropertiesDestructConfirm'
import { get } from 'vuex-pathify'
import { validationRules } from '@/common/validationRules'
import { format } from 'date-fns'
import { tableHeight } from '@/mixins/tableHeight'
import isEqual from 'lodash.isequal'

export default {
  name: 'LostPropertiesForm',

  mixins: [tableHeight(230)],

  props: {
    lostProperty: Object,
  },

  components: {
    LostPropertiesDestructConfirm,
  },

  data() {
    return {
      updating: false,
      showDestructModal: false,
      isValidForm: true,
      validationRules,
      category_group_uuid: null,
      origModel: {},
      model: {
        transportation_type_uuid: null,
        transportation_number: null,
        transportation_information: null,
        category_uuid: null,
        description: null,
        found_at: null,
      },
    }
  },

  created() {
    for (let prop in this.model) {
      this.category_group_uuid = this.lostProperty.category.category_group.uuid
      this.model[prop] = this.lostProperty[prop]
      this.model.found_at = format(
        new Date(this.lostProperty.found_at),
        "yyyy-MM-dd'T'HH:mm",
      )
      this.origModel[prop] = this.model[prop]
    }
  },

  computed: {
    owner: get('app/owner'),

    isFormChanges() {
      return !isEqual(this.model, this.origModel)
    },

    filteredSubCategories() {
      return (this.categories || []).filter(
        ({ category_group_uuid }) =>
          category_group_uuid === this.category_group_uuid,
      )
    },
  },

  apollo: {
    transportationTypes: {
      query: GET_TRANSPORTATION_TYPES,
    },

    categoryGroups: {
      query: GET_CATEGORY_GROUPS,
    },

    categories: {
      query: GET_CATEGORIES,
    },
  },

  methods: {
    async onUpdate() {
      const isValid = await this.$refs.form.validate()
      if (!isValid) return
      this.updating = true
      await this.updateLostProperty()
      this.origModel = Object.assign({}, this.model)
      this.updating = false
    },

    updateLostProperty() {
      return this.$apollo.mutate({
        mutation: UPDATE_LOST_PROPERTY,
        variables: {
          payload: {
            ...this.model,
            found_at: new Date(this.model.found_at),
          },
          uuid: this.$route.params.uuid,
        },
      })
    },
  },
}
</script>
